import {
  FONT_SIZE,
  FONT_WEIGHT,
  LINE_HEIGHT,
  SPACING,
  PADDING,
  MARGIN,
  GAP,
  COMPONENT_SIZES,
  Z_INDEX,
  BORDERS,
  ELEVATION_STYLES,
  BUTTON_BORDER_STYLES,
  TRANSITION_TIMING,
  TRANSITION_EASING,
  TRANSITIONS,
  ANIMATION_TIMING,
  ANIMATIONS,
  BREAKPOINTS_VALUES,
  MEDIA_QUERIES,
  ICON_SIZES,
  ICON_STROKE_WIDTH,
} from "../definitions";

export const LIQUID_GLASS_COLORS = {
  primary: "#0A0A0A",
  accent: "#0A0A0A",
  secondary: "rgba(10, 10, 10, 0.55)",
  warning: "#C0824A",
  discount: "#C0824A",

  white: "#FFFAF0",
  black: "#0A0A0A",

  greyLight: "rgba(255, 255, 255, 0.4)",
  greyMedium: "rgba(10, 10, 10, 0.25)",
  greyDark: "rgba(10, 10, 10, 0.45)",
  greyExtraDark: "rgba(10, 10, 10, 0.6)",

  success: "#4caf50",
  error: "#d32f2f",
  info: "#2196f3",

  overlay: {
    dark: "rgba(10,10,10,0.08)",
    darkStrong: "rgba(10,10,10,0.15)",
    light: "rgba(255,255,255,0.5)",
    veryLight: "rgba(255,255,255,0.25)",
  },

  text: {
    primary: "#0A0A0A",
    secondary: "rgba(10, 10, 10, 0.55)",
    disabled: "rgba(10, 10, 10, 0.3)",
    light: "rgba(10, 10, 10, 0.70)",
    lighter: "rgba(10, 10, 10, 0.50)",
  },

  background: {
    paper: "rgba(255, 250, 240, 0.55)",
    default: "#F5EDE0",
  },

  glass: {
    surface: "rgba(255, 250, 240, 0.55)",
    surfaceHover: "rgba(255, 250, 240, 0.72)",
    surfaceStrong: "rgba(255, 250, 240, 0.78)",
    border: "rgba(255, 255, 255, 0.55)",
    borderStrong: "rgba(255, 255, 255, 0.70)",
    shadow: "rgba(60, 40, 20, 0.18)",
    shadowStrong: "rgba(60, 40, 20, 0.28)",
    buttonDark: "rgba(10, 10, 10, 0.88)",
    buttonDarkText: "#FFFAF0",
    cream: "#F5EDE0",
    creamLight: "#FFFAF0",
  },
} as const;

const LIQUID_GLASS_FONT_FAMILY = {
  primary: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', system-ui, sans-serif",
  secondary: "-apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  mono: "ui-monospace, 'SF Mono', Menlo, monospace",
} as const;

const LIQUID_GLASS_BORDER_RADIUS = {
  none: "0",
  xs: "12px",
  sm: "16px",
  md: "24px",
  lg: "32px",
  xl: "40px",
  pill: "100px",
  round: "50%",
} as const;

const LIQUID_GLASS_SHADOWS = {
  none: "none",
  xs: "0 4px 12px rgba(60,40,20,0.08)",
  sm: "0 6px 20px -4px rgba(60,40,20,0.12)",
  md: "0 12px 40px -12px rgba(60,40,20,0.18), inset 0 1px 0 rgba(255,255,255,0.85)",
  lg: "0 16px 48px -12px rgba(60,40,20,0.22), inset 0 1px 0 rgba(255,255,255,0.90)",
  xl: "0 24px 64px -16px rgba(60,40,20,0.28), inset 0 1px 0 rgba(255,255,255,0.95)",
} as const;

export const THEME_LIQUID_GLASS = {
  name: "liquidGlass",
  label: "Semillando",

  colors: LIQUID_GLASS_COLORS,

  typography: {
    fontFamily: LIQUID_GLASS_FONT_FAMILY,
    fontSize: FONT_SIZE,
    fontWeight: FONT_WEIGHT,
    lineHeight: LINE_HEIGHT,
    styles: {
      h1: { fontSize: "2.5rem", fontWeight: 600, letterSpacing: "0.06em" },
      h2: { fontSize: "2rem", fontWeight: 600, letterSpacing: "0.04em" },
      h3: { fontSize: "1.5rem", fontWeight: 500, letterSpacing: "0.02em" },
      body: { fontSize: "1rem", fontWeight: 400, letterSpacing: "0.01em" },
      caption: { fontSize: "0.75rem", fontWeight: 400, letterSpacing: "0.04em" },
    },
  },

  spacing: {
    values: SPACING,
    padding: PADDING,
    margin: MARGIN,
    gap: GAP,
  },

  components: {
    sizes: COMPONENT_SIZES,
    zIndex: Z_INDEX,
  },

  visual: {
    shadows: LIQUID_GLASS_SHADOWS,
    borders: BORDERS,
    borderRadius: LIQUID_GLASS_BORDER_RADIUS,
    elevation: ELEVATION_STYLES,
    buttonBorders: BUTTON_BORDER_STYLES,
  },

  animation: {
    timing: TRANSITION_TIMING,
    easing: TRANSITION_EASING,
    transitions: TRANSITIONS,
    animationTiming: ANIMATION_TIMING,
    animations: ANIMATIONS,
  },

  responsive: {
    breakpoints: BREAKPOINTS_VALUES,
    mediaQueries: MEDIA_QUERIES,
  },

  icons: {
    sizes: ICON_SIZES,
    strokeWidth: ICON_STROKE_WIDTH,
  },
} as const;

export type ThemeLiquidGlass = typeof THEME_LIQUID_GLASS;
